import {useEffect, useState, useCallback, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import useTimeout from '../../../hooks/useTimeout';
import * as css from './FeedbackSection.css';
import LikeDislikeButtons from '../../../component/likeDislikeButtons/LikeDislikeButtons';
import { submitVideoFeedback } from '../../../redux/management/managementActions';
import { Button, getToken } from '@tickchak/react-share/app';
import SuccessCheckMark from '../../../assets/icons/successCheckMark';
import { TState } from '../../../redux/store.type';
import { useSelector } from 'react-redux';
import { XHR_SUCCESS_STATE, XHR_LOADING_STATE } from '../../../assets/constants/constants';
import {resetGlobalState} from '../../../redux/setGlobalState';

interface FeedbackSectionProps {
    videoId: number;
}

function FeedbackSection({videoId}: FeedbackSectionProps) {
    const {t} = useTranslation('layout');
    const [rating, setRatingState] = useState<'like' | 'dislike' | null>(null);
    const [isTextSectionOpen, setIsTextSectionOpen] = useState(false);
    const [text, setText] = useState('');
    const [isCanceled, setIsCanceled] = useState(false);
    const feedbackXHR = useSelector((state: TState) => (state.management.videoFeedbackXHR));
    const timer = useTimeout();
    const textSectionRef = useRef<HTMLDivElement | null>(null);

    const resetXHR = useCallback(() => {
        resetGlobalState('management.videoFeedbackXHR');
    }, []);

    const resetText = () => setText('');
    
    const handleRate = (newRating: 'like' | 'dislike') => {
        const finalRating = rating === newRating ? null : newRating;
        
        // If changing rating (not resetting), reset everything
        if (finalRating !== null && rating !== null && rating !== finalRating) {
            resetXHR();
            setIsCanceled(false);
            resetText();
        }
        
        // Update the rating
        setRatingState(finalRating);
        
        // Open the text box only if there is a rating
        if (finalRating !== null) {
            setIsTextSectionOpen(true);
            resetText();
            setIsCanceled(false);
            // Scroll to text section after it opens
            timer.current = setTimeout(() => {
                textSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest'
                });
            }, 200);
        } else {
            // If resetting the rating, close the text box
            setIsTextSectionOpen(false);
            resetXHR();
        }
        // Only open the text box, don't send a request
    };

    useEffect(() => {
        return () => {
            resetXHR();
        };
    }, []);

    const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setText(event.target.value);
    };

    const handleSave = async () => {
        if (rating) {
            setIsCanceled(false);
            await submitVideoFeedback(videoId, rating, text.trim());
            // Close the text box but keep the rating
            setIsTextSectionOpen(false);
        }
    };

    const handleCancel = async () => {
        if (rating) {
            const currentRating = rating;
            setIsCanceled(true);
            setIsTextSectionOpen(false); // Close the text box immediately
            await submitVideoFeedback(videoId, currentRating);
            resetXHR();
            // The rating remains visible
        }
    };

    return (
        <css.feedback>
            <css.feedbackSection>
                <css.feedbackText>
                    <strong>{t('wowYouMadeIt')}</strong>
                    <span>{t('didWeHelp')}</span>
                </css.feedbackText>
                    <LikeDislikeButtons
                        value={rating}
                        onChange={handleRate}
                    />
        </css.feedbackSection>
        <css.wrapAll $isOpen={isTextSectionOpen || (feedbackXHR === XHR_SUCCESS_STATE && !isCanceled)}>
        {feedbackXHR === XHR_SUCCESS_STATE && !isCanceled ? (<css.successMessage>
                <SuccessCheckMark backgroundColor={getToken('color.state.success.bg')} color={getToken('color.state.success.main')} />
                <span>{t('feedbackReceived')}</span>
            </css.successMessage>
        ) : (<>
                    <css.feedbackSectionText ref={textSectionRef}>
                        <css.title>{t('weAreOpenToHear')}</css.title>
                        <css.wrapAllTextarea>
                            <textarea 
                                value={text} 
                                onChange={handleInput}
                                placeholder={t('ratingFeedbackPlaceholder')}
                            />
                        </css.wrapAllTextarea>
                    </css.feedbackSectionText>
                    <css.buttons>
                        <Button 
                            className={'cancel'} 
                            onClick={handleCancel} 
                            disabled={feedbackXHR === XHR_LOADING_STATE}
                            size="category"
                        >
                            {t('cancelBtn')}
                        </Button>
                        <Button 
                            onClick={handleSave} 
                            disabled={feedbackXHR === XHR_LOADING_STATE}
                            variant="brand"
                            size="category"
                        >
                            {t('sendFeedback')}
                        </Button>
                    </css.buttons>
                    </>)}
            </css.wrapAll>
        </css.feedback>
    );
}

export default FeedbackSection;
